import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

import { environment } from '../../environments/environment.prod';
import { environment as buildEnvironment } from '../../environments/environment';

export interface SocketResponse {
  data: any;
  status: number;
  event: string;
  params: any;
}

export interface SocketRequest {
  event: string;
  params: any;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  public socket: WebSocket;
  public urlConnect: string;
  public tryConnectCount: number;
  public tryConnectLimit: number;
  public subscribers = {};
  public doConnectedEvents = [];
  public isSocketCloseManual: boolean;

  constructor() {
    let l = window.location;
    let postfix = '/async/ws/chat';

    this.urlConnect = (l.protocol === 'http:' ? 'ws:' : 'wss:') + '//' + l.host + environment.apiPrefix + postfix;
    this.tryConnectLimit = 10;
    this.isSocketCloseManual = false;
  }

  /**
   * Геттер. Возвращает true если соединени открыто и готово к использованию.
   * @property isOpened
   * @returns {boolean}
   */
  get isOpened() {
    return !!this.socket && this.socket.readyState === 1;
  }

  /**
   *  Отправляет данные на сервер
   *
   *  @method emit
   *  @param requestData
   *  {
   *    event: 'notifications',                                 # имя события
   *    params: {request_id: 'xyz', user_id: 3, sector_id: 4}   # входные параметры запроса
   *    }
   */
  emit(requestData: SocketRequest) {
    if (this.isOpened) {
      this.socket.send(JSON.stringify(requestData));
    } else {
      this.connect();
      this.doConnectedEvents.push(requestData);
    }
  }

  /**
   * Подписка на события от сокета.
   *
   * @method subscribe
   * @param eventType - имя события
   * @param callback - функция обработчик
   * @returns {observer}
   */
  subscribe(eventType, callback) {
    if (!this.subscribers) {
      this.subscribers = {};
    }
    if (!this.subscribers[eventType]) {
      this.subscribers[eventType] = <ReplaySubject<any>>new ReplaySubject(null);
    }

    return this.subscribers[eventType].subscribe(callback);
  }

  /**
   * Обработчик на пришедшие данные от сервера
   *
   * @private
   * @method _responseHandler
   * @param responseData
   * {
   *    event: 'notifications',                                   # имя события
   *    params: {request_id: 'xyz', user_id: 3, sector_id: 4},    # входные параметры при запроса
   *    status: 200,                                              # статус события, 200 - ок
   *    data: someData # {...}                                    # данные события. Могут быть в любом формате
   * }
   */
  _responseHandler(responseData: string) {
    let response = JSON.parse(responseData);

    if (response) {
      let { data, event, params, status } = JSON.parse(responseData);

      if (event && status < 299 && this.subscribers[event]) {
        this.subscribers[event].next({ data, event, params, status });
      }
    }
  }

  /**
   *  Делает подключение к сокету, вешает обработчики на события
   *
   *  @method connect
   */
  connect() {
    // Build optimization must not enable production-only connections on the test stand.
    if (!buildEnvironment.production) {
      return;
    }
    if (this.socket && this.isOpened) {
      return;
    }

    this.socket = new WebSocket(this.urlConnect);

    this.socket.onmessage = (event) => {
      this._responseHandler(event.data);
    };

    // после открытия сокета отправляет скопившиеся таски
    this.socket.onopen = () => {
      console.log('WebSocket connection created.');

      this.doConnectedEvents.forEach((request) => {
        this.emit(request);
      });
      this.doConnectedEvents = [];

      this.tryConnectCount = 0; // обнуление попыпыток подключений
      this.isSocketCloseManual = false; // обнуление флага ручной остановки сокета
    };

    this.socket.onclose = (event) => {
      console.warn('WebSocket connection closed.', event);

      if (!this.isSocketCloseManual) {
        this._reconnect();
      }
    };

    this.socket.onerror = (event) => {
      console.error('WebSocket connection has error.', event);
      this._reconnect();
    };
  }

  /**
   *  Закрывает соединение с сокетом
   *
   *  @method close
   */
  close() {
    if (this.socket && this.isOpened) {
      this.socket.close();
    }

    this.isSocketCloseManual = true;
  }

  /**
   *  Делает реконет к серверу
   *
   *  @private
   *  @method _reconnect
   */
  _reconnect() {
    let oldSocket = this.socket; // иногда возникают оба события: и onclose и onerror, в этом случае нужно предотвратить
    // попытку создать два подключения после срабатывания обоих таймеров
    setTimeout(() => {
      if (oldSocket === this.socket && this.tryConnectCount < this.tryConnectLimit) {
        this.tryConnectCount++;
        console.warn(`WebSocket try reconnect. attempt ${this.tryConnectCount} of ${this.tryConnectLimit}`);
        this.connect();
      }
    }, 5000);
  }
}
